import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useMemo } from 'react';
import { formatDifferentialPrice } from '../../utils/formatter.util.js';
import { Typography, ErrorBoundary } from '@vp/swan';
import { clsx } from '../../external/clsx/dist/clsx.js';
import styles from './differential-price.module.scss.js';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { ZeroDisplayType } from '../../types/differential-price.types.js';
import { usePriceLabels } from '../../utils/priceLabels.js';

const DifferentialPriceInternal = forwardRef(({ className, differentialPriceConfig, ...rest }, ref) => {
  const {
    locale,
    currency,
    minimumFractionDigits,
    priceDifference,
    priceDifferenceDiscounted,
    showComparativeDisplay = true,
    zeroDisplayType = ZeroDisplayType.Included
  } = differentialPriceConfig;
  const { t } = useLocalization(locale);
  const classNames = clsx(className, styles.differentialPrice);
  const differentialListPrice = useMemo(
    () => formatDifferentialPrice(locale, currency, minimumFractionDigits, priceDifference),
    [locale, currency, minimumFractionDigits, priceDifference]
  );
  const differentialDiscountedPrice = useMemo(
    () => priceDifferenceDiscounted !== void 0 ? formatDifferentialPrice(locale, currency, minimumFractionDigits, priceDifferenceDiscounted) : void 0,
    [locale, currency, minimumFractionDigits, priceDifferenceDiscounted]
  );
  const { OriginalPriceLabel, SalePriceLabel } = usePriceLabels(locale);
  if (priceDifference === 0) {
    if (zeroDisplayType === ZeroDisplayType.None) {
      return null;
    } else if (zeroDisplayType === ZeroDisplayType.Zero) {
      return /* @__PURE__ */ jsx(Typography, { as: "span", ref, className: clsx(classNames), ...rest, children: differentialListPrice });
    }
    return /* @__PURE__ */ jsx(Typography, { as: "span", ref, className: clsx(classNames), ...rest, children: t("differentialPrice.included") });
  }
  if (priceDifferenceDiscounted && priceDifferenceDiscounted < priceDifference) {
    if (showComparativeDisplay) {
      return /* @__PURE__ */ jsxs(Typography, { as: "span", ref, className: clsx(classNames), ...rest, children: [
        OriginalPriceLabel,
        /* @__PURE__ */ jsx("s", { children: differentialListPrice }),
        /* @__PURE__ */ jsxs(Typography, { as: "span", textColor: "promo", fontWeight: "bold", pl: 2, children: [
          SalePriceLabel,
          differentialDiscountedPrice
        ] })
      ] });
    } else {
      return /* @__PURE__ */ jsx(Typography, { as: "span", ref, textColor: "promo", fontWeight: "bold", className: clsx(classNames), ...rest, children: differentialDiscountedPrice });
    }
  }
  return /* @__PURE__ */ jsx(Typography, { as: "span", ref, className: clsx(classNames), ...rest, children: differentialListPrice });
});
const DifferentialPrice = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(ErrorBoundary, { fallback: /* @__PURE__ */ jsx(Typography, { as: "span", children: props.placeholdertext }), children: /* @__PURE__ */ jsx(DifferentialPriceInternal, { ...props, ref }) });
});

export { DifferentialPrice, DifferentialPriceInternal };
